import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from 'config/axiosConfig';
import { User } from 'util/constants';
import { isMobile, isBrowser } from 'react-device-detect';
import AuthService from 'services/authService';
import Cookies from 'js-cookie';
import { apiRoutes } from "util/apiRoutes";

interface UserContextProps {
  currentUser: User | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
  awaitingUser: boolean;
  showAuthModal: boolean;
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Controls which auth mode the `AuthModal` should default to when opened.
   * - "signup": forces the modal into sign-up/register mode (ignores existing-account cookie/localStorage).
   * - "signin": forces the modal into sign-in mode.
   * - null: modal decides based on existing-account heuristics.
   */
  authModalMode: "signin" | "signup" | null;
  setAuthModalMode: React.Dispatch<React.SetStateAction<"signin" | "signup" | null>>;
  showUsernameModal: boolean;
  setShowUsernameModal: React.Dispatch<React.SetStateAction<boolean>>;
  showUserOnboardModal: boolean;
  setShowUserOnboardModal: React.Dispatch<React.SetStateAction<boolean>>;
  // Controls which sub-view the onboarding modal should open to initially
  userOnboardInitialView: "issues" | null;
  setUserOnboardInitialView: React.Dispatch<React.SetStateAction<"issues" | null>>;
  userOnboardingTasks: number | null;
  setUserOnboardingTasks: React.Dispatch<React.SetStateAction<number | null>>;
  refreshUser: () => Promise<void>;
  /**
   * Device-based mobile flag derived from `react-device-detect` (user-agent
   * sniff). Use for **device-affordance** decisions (drawer vs modal, touch
   * tap targets, native-app-style navigation), not for responsive layout.
   *
   * For viewport-responsive layout decisions — anything that should react to
   * resize, orientation changes, or dev-tools mobile emulation — reach for
   * `useMobileViewport()` from `hooks/useMobileViewport` instead. That hook
   * tracks `matchMedia("(max-width: 767px)")` reactively and returns the
   * correct value on the first render (no layout flash).
   */
  userIsMobile: boolean;
  userIsBrowser: boolean;
}

interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [awaitingUser, setAwaitingUser] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signin" | "signup" | null>(null);
  const [userIsMobile, setUserIsMobile] = useState(false);
  const [userIsBrowser, setUserIsBrowser] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showUserOnboardModal, setShowUserOnboardModal] = useState(false);
  const [userOnboardInitialView, setUserOnboardInitialView] = useState<"issues" | null>(null);
  const [userOnboardingTasks, setUserOnboardingTasks] = useState<number | null>(null);
  const fetchUser = async () => {
    try {
      const response = await api.get(apiRoutes.user.root);
      const userData = {
        ...response.data,
        id: parseInt(response.data.id, 10)
      };
      setCurrentUser(userData);
    } catch (error: any) {
      // Only clear current user if it's an auth error
      if (error.response?.status === 401) {
        setCurrentUser(null);
      }
    } finally {
      setAwaitingUser(false);
    }
  };

  const refreshUser = async () => {
    setAwaitingUser(true);
    await fetchUser();
  };

  useEffect(() => {
    if (isMobile) {
      setUserIsMobile(true);
    } else if (isBrowser) {
      setUserIsBrowser(true);
    }
  }, []);

  useEffect(() => {
    const cookieValue = Cookies.get('userOnboardingTasks');
    if (cookieValue !== undefined) {
        setUserOnboardingTasks(parseInt(cookieValue, 10));
    }
}, []);

  // Initial auth check
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await Promise.allSettled([
          AuthService.ensureCsrfToken(),
          AuthService.refresh(true),
        ]);
      } catch (error) {
        // If refresh fails, we'll proceed to fetch user anyway
        console.error('Error refreshing token:', error);
      } finally {
        // Always try to fetch user info
        await fetchUser();
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (currentUser) {
      setAwaitingUser(false);
      setShowAuthModal(false);
    }
  }, [currentUser]);

  /**
   * Safety: avoid leaking a previously-forced auth mode into future modal opens.
   * When the modal is not open, always clear the forced mode.
   */
  useEffect(() => {
    if (!showAuthModal && authModalMode !== null) {
      setAuthModalMode(null);
    }
  }, [showAuthModal, authModalMode]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        awaitingUser,
        showAuthModal,
        setShowAuthModal,
        authModalMode,
        setAuthModalMode,
        showUsernameModal,
        setShowUsernameModal,
        showUserOnboardModal,
        setShowUserOnboardModal,
        userOnboardInitialView,
        setUserOnboardInitialView,
        userOnboardingTasks,
        setUserOnboardingTasks,
        refreshUser,
        userIsMobile,
        userIsBrowser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
